import { initFlatpickrRange } from '@core/component/date-picker/flatpickr-range.js';

/**
 * Main Alpine component for the activity registration modal.
 * Business logic (available days, short/long range, single day) is handled
 * server-side and rendered as a fragment in #dateOptionsSection.
 * This component only manages Flatpickr and the submit-button state.
 */
export default () => ({
    submitEnabled: false,

    init() {
        const form = this.$el.querySelector('#finalRegistrationForm');

        const defaultFrom    = form?.dataset.defaultFrom    || null;
        const defaultTo      = form?.dataset.defaultTo      || null;
        const dateOptionsUrl = form?.dataset.dateOptionsUrl || null;

        const activityId = form?.querySelector('input[name="activityId"]')?.value;
        const childId    = form?.querySelector('input[name="childId"]')?.value;

        // Parse an ISO date string (YYYY-MM-DD) into a local Date object.
        // Flatpickr's minDate/maxDate must be Date objects when dateFormat is "d.m.Y",
        // otherwise Flatpickr misparses the ISO string using the display format.
        const parseIso = (str) => {
            if (!str) return null;
            const [y, m, d] = str.split('-').map(Number);
            return new Date(y, m - 1, d);
        };

        const parsedMin = parseIso(form?.dataset.minDate);
        const parsedMax = parseIso(form?.dataset.maxDate);

        const registrationDateRange = this.$el.querySelector('#registrationDateRange');

        initFlatpickrRange(registrationDateRange, {
            // flatpickr-range.js already handles ISO strings for defaultDate via parseISODate
            defaultDate:  (defaultFrom && defaultTo) ? [defaultFrom, defaultTo] : null,
            fromInput:    this.$el.querySelector('#registrationDateFrom'),
            toInput:      this.$el.querySelector('#registrationDateTo'),
            clearOnOpen:  false,
            extraConfig:  { minDate: parsedMin, maxDate: parsedMax },
            onClose: (selectedDates) => {
                if (selectedDates.length === 2) {
                    const [from, to] = selectedDates;
                    this._refreshDateOptions(dateOptionsUrl, activityId, childId, from, to);
                }
                this.validateForm();
            },
            onChange: (selectedDates) => {
                if (selectedDates.length === 2) {
                    this.validateForm();
                }
            },
        });

        // Re-evaluate submit button after HTMX swaps #dateOptionsSection
        this.$el.addEventListener('htmx:afterSwap', () => {
            this.$nextTick(() => this.validateForm());
        });

        // Re-evaluate when the user toggles an individual day
        this.$el.addEventListener('days-changed', () => this.validateForm());

        this.validateForm();
    },

    /**
     * Refreshes #dateOptionsSection via HTMX GET for the new date range.
     * URL params are built manually to ensure correct GET query string.
     * Target is resolved as a DOM element to avoid CSS-selector lookup issues.
     */
    _refreshDateOptions(url, activityId, childId, from, to) {
        if (!url || !activityId || !childId) return;

        const target = this.$el.querySelector('#dateOptionsSection');

        const pad    = (n) => String(n).padStart(2, '0');
        const toIso  = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        const params = new URLSearchParams({ activityId, childId, dateFrom: toIso(from), dateTo: toIso(to) });

        htmx.ajax('GET', `${url}?${params}`, {
            target: target,
            swap:   'outerHTML',
        });
    },

    validateForm() {
        const section = this.$el.querySelector('#dateOptionsSection');
        // Submit is enabled when at least one day is available and selected.
        // If weekDayOptions was empty (no days for this period), the hidden input
        // is not rendered and selectedDays is empty -> button stays disabled.
        const hiddenInput  = section?.querySelector('input[name="selectedWeekDays"]');
        const selectedDays = hiddenInput?.value || '';
        this.submitEnabled = selectedDays.length > 0;
    },
});
